/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-argument */
import { useCallback } from 'react';
import { useOrganizationsApi } from 'services/api';
import useSWR, { mutate } from 'swr';
import { MemberRequest } from 'services/api/apis/organizations/organizations.api';
import { useMarkOrganizationFollowed } from 'state/followed-organizations/followed-organizations.state';

export interface UseMyMembershipRequestResult {
  data: MemberRequest | null | undefined;
  error: Error | undefined;
  isValidating: boolean;
  revalidate: () => void;
  /**
   * Optimistically sets the SWR cache. Use after create/cancel/leave so the
   * UI swaps states instantly without waiting for the next GET.
   */
  setCache: (next: MemberRequest | null) => void;
}

/**
 * Fetches the current user's pending membership request for the given organization.
 * Returns `null` when there is no pending request (and `undefined` while loading).
 */
export const useMyMembershipRequest = (
  open: boolean,
  selectedOrganizationId: string,
): UseMyMembershipRequestResult => {
  const organizationsApi = useOrganizationsApi();
  const swrKey = open && selectedOrganizationId
    ? [`/organizations/${selectedOrganizationId}/my-membership-request`]
    : null;

  const rMembershipRequest = useSWR<MemberRequest | null>(
    swrKey,
    async () => {
      const result = await organizationsApi.getMyMembershipRequestForOrg(
        selectedOrganizationId,
      );
      if (result.success) return result.value;
      return null;
    },
  );

  const revalidate = useCallback(() => {
    if (swrKey)
      void mutate(swrKey);
  }, [swrKey]);

  const setCache = useCallback(
    (next: MemberRequest | null) => {
      if (swrKey)
        void mutate(swrKey, next, false);
    },
    [swrKey],
  );

  return {
    data: rMembershipRequest.data,
    error: rMembershipRequest.error as Error | undefined,
    isValidating: rMembershipRequest.isValidating,
    revalidate,
    setCache,
  };
};

interface UseMembershipRequestActionsParams {
  selectedOrganizationId: string;
  myMembershipRequest: MemberRequest | null | undefined;
  revalidateMembershipRequest: () => void;
  /** Optimistically primes the SWR cache (fast UI swap, no refetch). */
  setMembershipRequestCache?: (next: MemberRequest | null) => void;
  /** Called after the follower state changes (approval, leave org). */
  revalidateOrganization?: () => void;
  onMembershipChange?: () => void;
}

export const useMembershipRequestActions = ({
  selectedOrganizationId,
  myMembershipRequest,
  revalidateMembershipRequest,
  setMembershipRequestCache,
  revalidateOrganization,
  onMembershipChange,
}: UseMembershipRequestActionsParams) => {
  const organizationsApi = useOrganizationsApi();
  const markFollowed = useMarkOrganizationFollowed();

  const handleRequestMembership = useCallback(async () => {
    const result = await organizationsApi.requestMembership(selectedOrganizationId);
    if (result.success) {
      setMembershipRequestCache?.(result.value);
      // Auto-accept join upserts a follower. Recoil (not the org SWR payload)
      // drives the Follow CTA, so update it immediately — otherwise Follow
      // stays until a full page refresh hydrates lists.
      if (result.value.status === 'approved') {
        markFollowed(selectedOrganizationId, true);
      } else {
        revalidateMembershipRequest();
      }
      revalidateOrganization?.();
    } else {
      throw new Error(result.error.message);
    }
  }, [
    organizationsApi,
    selectedOrganizationId,
    setMembershipRequestCache,
    revalidateMembershipRequest,
    revalidateOrganization,
    markFollowed,
  ]);

  const handleCancelMembershipRequest = useCallback(async () => {
    if (!myMembershipRequest?.id) return;
    const result = await organizationsApi.cancelMembershipRequest(
      myMembershipRequest.id,
    );
    if (result.success) {
      setMembershipRequestCache?.(null);
      revalidateMembershipRequest();
      revalidateOrganization?.();
    } else {
      throw new Error(result.error.message);
    }
  }, [
    organizationsApi,
    myMembershipRequest?.id,
    setMembershipRequestCache,
    revalidateMembershipRequest,
    revalidateOrganization,
  ]);

  /**
   * "Leave Organization" button for confirmed members.
   * Approval promoted the user to a follower, so leaving = unfollow.
   */
  const handleLeaveOrganization = useCallback(async () => {
    const result = await organizationsApi.unfollowOrganization(selectedOrganizationId);
    if (result.success) {
      setMembershipRequestCache?.(null);
      markFollowed(selectedOrganizationId, false);
      revalidateMembershipRequest();
      revalidateOrganization?.();
      onMembershipChange?.();
    } else {
      throw new Error(result.error.message);
    }
  }, [
    organizationsApi,
    selectedOrganizationId,
    setMembershipRequestCache,
    revalidateMembershipRequest,
    revalidateOrganization,
    onMembershipChange,
    markFollowed,
  ]);

  return {
    handleRequestMembership,
    handleCancelMembershipRequest,
    handleLeaveOrganization,
  };
};
